import React from "react";

import { DateTime } from "luxon";

import styled from "@mui/material/styles/styled";
import {
  BaseSingleInputFieldProps,
  TimePickerProps,
  FieldSection,
  TimePicker as MuiTimePicker,
  TimeField,
  UseTimeFieldProps,
  TimeValidationError,
  TimeFieldProps,
} from "@mui/x-date-pickers";

import useAnchorElement from "@app/utils/hooks/useAnchorElement";

const TimeFieldWithRef = React.forwardRef<HTMLDivElement>((props, ref) => (
  <TimeField ref={ref} {...props} />
));

TimeFieldWithRef.displayName = "TimeField";

const StyledTimeField = styled((props: TimeFieldProps<DateTime>) => (
  <TimeFieldWithRef {...props} />
))(({ theme }) => ({
  ".MuiInputBase-input": {
    fontSize: "14px",
    height: "32px",
    "::placeholder": {
      color: theme.palette.shades.neutral[800],
      opacity: 1,
    },
    padding: 0,
    textAlign: "center",
  },
  ".MuiInputBase-formControl": {
    backgroundColor: "#FFF",
    maxWidth: "130px",
    fontWeight: 400,
    height: "32px",
    "&.Mui-disabled": {
      backgroundColor: theme.palette.shades.neutral[50],
    },
  },
  ".MuiOutlinedInput-notchedOutline": {
    height: "37px",
    borderColor: theme.palette.shades.neutral[100],
    borderRadius: "4px",
    color: theme.palette.shades.neutral[800],
  },
}));

interface CustomTimeFieldProps
  extends UseTimeFieldProps<DateTime, true>,
    BaseSingleInputFieldProps<
      DateTime | null,
      DateTime,
      FieldSection,
      true,
      TimeValidationError
    > {
  onClick?: () => void;
}

const CustomTimeField = React.forwardRef<HTMLDivElement>(
  (
    {
      onClick,
      id,
      value,
      placeholder,
      disabled,
      onChange,
      inputProps: { "aria-label": ariaLabel } = {},
      format,
    }: CustomTimeFieldProps & { placeholder?: string },
    ref
  ) => {
    return (
      <StyledTimeField
        id={id}
        inputProps={{ placeholder }}
        disabled={disabled}
        ref={ref}
        aria-label={ariaLabel}
        onClick={onClick}
        value={value}
        onChange={(newValue, ctx) => onChange?.(newValue, ctx)}
        format={format}
      />
    );
  }
);
CustomTimeField.displayName = "CustomTimeField";

const CustomInputTimePicker = ({
  placeholder,
  format,
  ...props
}: Omit<TimePickerProps<DateTime>, "open" | "onOpen" | "onClose"> & {
  placeholder?: string;
}) => {
  const {
    open,
    anchorElement: anchorEl,
    onClick: onClickAnchorElement,
    onClose,
  } = useAnchorElement();

  const handleClick = (e: any) => {
    onClickAnchorElement(e);
  };

  return (
    <MuiTimePicker
      slots={{
        field: CustomTimeField,
        ...props.slots,
      }}
      slotProps={{
        field: { onClick: handleClick, placeholder, format } as any,
        popper: { anchorEl },
      }}
      thresholdToRenderTimeInASingleColumn={100}
      timeSteps={{ minutes: 30 }}
      {...props}
      open={open}
      onClose={onClose}
    />
  );
};

const TimePicker = ({
  format = "hh:mm a",
  placeholder,
  value,
  onChange,
  ...props
}: TimePickerProps<DateTime> & { placeholder?: string }) => {
  return (
    <CustomInputTimePicker
      format={format}
      placeholder={placeholder}
      value={value}
      onChange={onChange}
      {...props}
    />
  );
};

export default TimePicker;
